import dayjs from 'dayjs';
import type { PicqerLocationStockHistoryEntry } from './types';
import { ANALYSE_SINCE_DATE, ANALYSE_UNTIL_DATE } from './types';

export interface ActivePeriod {
  start: dayjs.Dayjs;
  end: dayjs.Dayjs | null;
}

export function parseChangedAt(value: string): dayjs.Dayjs {
  return dayjs(value.replace(' ', 'T'));
}

export function buildMonthKeys(from?: string, to?: string): string[] {
  const start = dayjs(from ?? '2025-01-01').startOf('month');
  const end = dayjs(to ?? '2026-06-01').startOf('month');
  const keys: string[] = [];
  let cursor = start;
  while (cursor.isBefore(end) || cursor.isSame(end, 'month')) {
    keys.push(cursor.format('YYYY-MM'));
    cursor = cursor.add(1, 'month');
  }
  return keys;
}

export const ALL_MONTH_KEYS = buildMonthKeys(
  ANALYSE_SINCE_DATE.slice(0, 10),
  ANALYSE_UNTIL_DATE.slice(0, 10),
);

export function buildActivePeriods(
  history: PicqerLocationStockHistoryEntry[],
  preRangeStock: number,
  rangeStart: dayjs.Dayjs,
  rangeEnd: dayjs.Dayjs,
): ActivePeriod[] {
  const sorted = [...history].sort(
    (a, b) => parseChangedAt(a.changed_at).valueOf() - parseChangedAt(b.changed_at).valueOf(),
  );

  const periods: ActivePeriod[] = [];
  let stock = preRangeStock;
  let periodStart: dayjs.Dayjs | null = stock > 0 ? rangeStart : null;

  for (const entry of sorted) {
    const at = parseChangedAt(entry.changed_at);
    if (at.isBefore(rangeStart) || at.isAfter(rangeEnd)) continue;

    const prevStock = stock;
    stock = entry.new_stock;

    if (prevStock <= 0 && stock > 0) {
      periodStart = at;
    } else if (prevStock > 0 && stock <= 0 && periodStart) {
      periods.push({ start: periodStart, end: at });
      periodStart = null;
    }
  }

  if (periodStart && stock > 0) {
    periods.push({ start: periodStart, end: null });
  }

  return periods;
}

export function occupiedFractionInMonth(
  periods: ActivePeriod[],
  monthStart: dayjs.Dayjs,
  monthEnd: dayjs.Dayjs,
  rangeEnd: dayjs.Dayjs,
): number {
  const windowStart = monthStart.startOf('day').valueOf();
  const windowEnd = monthEnd.endOf('day').valueOf();
  const monthDurationMs = windowEnd - windowStart + 1;
  if (monthDurationMs <= 0) return 0;

  let overlapMs = 0;
  for (const period of periods) {
    const periodEnd = period.end ?? rangeEnd;
    const overlapStart = Math.max(period.start.valueOf(), windowStart);
    const overlapEnd = Math.min(periodEnd.valueOf(), windowEnd);
    if (overlapEnd > overlapStart) overlapMs += overlapEnd - overlapStart;
  }

  return Math.min(1, overlapMs / monthDurationMs);
}

export function stockAtDate(
  history: PicqerLocationStockHistoryEntry[],
  preRangeStock: number,
  at: dayjs.Dayjs,
  rangeStart: dayjs.Dayjs,
): number {
  const sorted = [...history]
    .filter((e) => {
      const t = parseChangedAt(e.changed_at);
      return !t.isBefore(rangeStart) && !t.isAfter(at);
    })
    .sort((a, b) => parseChangedAt(a.changed_at).valueOf() - parseChangedAt(b.changed_at).valueOf());

  if (sorted.length === 0) return preRangeStock;
  return sorted[sorted.length - 1].new_stock;
}

export function isUsedAtMonthEnd(
  history: PicqerLocationStockHistoryEntry[],
  preRangeStock: number,
  monthEnd: dayjs.Dayjs,
  rangeStart: dayjs.Dayjs,
): boolean {
  return stockAtDate(history, preRangeStock, monthEnd.endOf('day'), rangeStart) > 0;
}

export interface LocationMonthMetric {
  month: string;
  fraction: number;
  used: boolean;
}

export function computeLocationMonthlyMetrics(
  history: PicqerLocationStockHistoryEntry[],
  preRangeStock: number,
  monthKeys: string[],
  metricMode: 'avg-month-occupation' | 'any-time-in-month' | 'month-end-snapshot',
): LocationMonthMetric[] {
  const rangeStart = dayjs(ANALYSE_SINCE_DATE.replace(' ', 'T'));
  const rangeEnd = dayjs(ANALYSE_UNTIL_DATE.replace(' ', 'T'));
  const periods = buildActivePeriods(history, preRangeStock, rangeStart, rangeEnd);

  return monthKeys.map((monthKey) => {
    const monthStart = dayjs(`${monthKey}-01`).startOf('month');
    const monthEnd = monthStart.endOf('month');

    if (metricMode === 'month-end-snapshot') {
      const used = isUsedAtMonthEnd(history, preRangeStock, monthEnd, rangeStart);
      return { month: monthKey, fraction: used ? 1 : 0, used };
    }

    const fraction = occupiedFractionInMonth(periods, monthStart, monthEnd, rangeEnd);
    const used = fraction > 0;
    return { month: monthKey, fraction, used };
  });
}
